// Player morale L (research/trade-value-spec.md §4), derived from ZenGM's existing mood
// system rather than a parallel state machine.
//
// ZenGM already computes dynamic mood components per player+team — teamPerformance
// (winning), playingTime (role), loyalty (tenure), marketSize — and updates them live. We
// collapse those into an L in [0,1] (the player's desire to STAY) and use it two ways in the
// owning team's trade valuation:
//   - lfactor(L): scales the belovedness premium down as morale falls, so a disgruntled
//     cornerstone stops being untouchable (the front office's hand is forced).
//   - walkFraction(L, yearsLeft): a disgruntled player on an expiring deal is worth less to
//     keep — you'd rather trade him for assets than lose him for nothing.

import type { MoodComponents } from "../../../common/types.ts";

/** Collapse ZenGM's mood components for a player's current team into L in [0,1]. */
export const computeMoraleL = (components: MoodComponents): number => {
	let sum = 0;
	for (const key of Object.keys(components) as (keyof MoodComponents)[]) {
		if (key === "custom") {
			for (const row of components.custom ?? []) {
				sum += row.amount;
			}
		} else {
			sum += components[key];
		}
	}
	// Sigmoid: a content player (positive mood sum) sits high, a disgruntled one low.
	return 1 / (1 + Math.exp(-0.4 * sum));
};

// Below FULL, the belovedness premium starts eroding; at MIN it's gone.
const LFACTOR_MIN = 0.15;
const LFACTOR_FULL = 0.6;

/** Belovedness-premium multiplier: 1 when content, ~0 once morale collapses. */
export const lfactor = (L: number): number =>
	Math.max(0, Math.min(1, (L - LFACTOR_MIN) / (LFACTOR_FULL - LFACTOR_MIN)));

const WALK_MAX = 0.6; // most of a player's keep-value that walk risk can erase

/** Fraction of keep-value lost to walk risk, larger as morale falls and the deal expires. */
export const walkFraction = (L: number, contractYearsLeft: number): number => {
	const expiringMult = contractYearsLeft <= 1 ? 1 : contractYearsLeft <= 2 ? 0.4 : 0.1;
	const pWalk = Math.max(0, Math.min(1, (1 - L) * expiringMult));
	return Math.min(WALK_MAX, pWalk * WALK_MAX);
};
